import L from 'leaflet';
import {
  AREA_TYPES, DEFAULT_LINE_WIDTH, getAreaTypeColor, getAreaTypeLabel,
} from '../../SiteTakeoff/siteTakeoffConstants';
import { enhancedGetPolygonArea } from '../../MapWithDrawing/Measure/mapUtils';

const DEFAULT_POLYGON_TYPE = AREA_TYPES.BD_SHRUB_MED;
const DEFAULT_LINE_TYPE = AREA_TYPES.TR_DG;
const DEFAULT_POINT_TYPE = AREA_TYPES.POINT_MARKER;

const childrenByLocalName = (node, localName) => (
  Array.from(node?.children || []).filter(child => child.localName === localName)
);

const firstChildText = (node, localName) => (
  childrenByLocalName(node, localName)[0]?.textContent?.trim() || ''
);

const descendantsByLocalName = (node, localName) => (
  Array.from(node?.getElementsByTagName('*') || []).filter(child => child.localName === localName)
);

const firstDescendantText = (node, localName) => (
  descendantsByLocalName(node, localName)[0]?.textContent?.trim() || ''
);

const isFinitePoint = (point) => (
  Number.isFinite(point?.lat) && Number.isFinite(point?.lng)
);

const samePoint = (a, b) => (
  Math.abs(Number(a?.lat) - Number(b?.lat)) < 0.00000001
  && Math.abs(Number(a?.lng) - Number(b?.lng)) < 0.00000001
);

const parseCoordinateText = (coordinateText) => (
  String(coordinateText || '')
    .trim()
    .split(/\s+/)
    .map(token => {
      const [lng, lat] = token.split(',').map(Number);
      return { lat, lng };
    })
    .filter(isFinitePoint)
);

const stripClosedRingDuplicate = (paths) => {
  if (!Array.isArray(paths) || paths.length < 2) return paths || [];
  if (!samePoint(paths[0], paths[paths.length - 1])) return paths;
  return paths.slice(0, -1);
};

const lineLengthFeet = (paths) => {
  if (!paths || paths.length < 2) return 0;
  let totalMeters = 0;
  for (let i = 1; i < paths.length; i += 1) {
    const previous = L.latLng(paths[i - 1].lat, paths[i - 1].lng);
    const current = L.latLng(paths[i].lat, paths[i].lng);
    totalMeters += previous.distanceTo(current);
  }
  return totalMeters * 3.28084;
};

const buildPolygonArea = (name, paths) => {
  const areaType = DEFAULT_POLYGON_TYPE;
  const measurements = enhancedGetPolygonArea(paths);
  return {
    geometryType: 'polygon',
    name: name || getAreaTypeLabel(areaType),
    areaType,
    paths,
    area: (measurements.areaFeet || 0).toFixed(2),
    areaAcres: (measurements.areaAcres || 0).toFixed(4),
    perimeter: (measurements.perimeterFeet || 0).toFixed(2),
    fillColor: getAreaTypeColor(areaType),
    strokeColor: '#000000',
  };
};

const buildPolylineArea = (name, paths) => {
  const areaType = DEFAULT_LINE_TYPE;
  const lengthFeet = lineLengthFeet(paths);
  const areaSF = lengthFeet * DEFAULT_LINE_WIDTH;
  return {
    geometryType: 'polyline',
    name: name || getAreaTypeLabel(areaType),
    areaType,
    paths,
    length: lengthFeet.toFixed(2),
    width: DEFAULT_LINE_WIDTH,
    area: areaSF.toFixed(2),
    areaAcres: (areaSF / 43560).toFixed(4),
    perimeter: '0',
    fillColor: getAreaTypeColor(areaType),
    strokeColor: '#000000',
  };
};

const buildPointArea = (name, paths) => {
  const areaType = DEFAULT_POINT_TYPE;
  return {
    geometryType: 'point',
    name: name || getAreaTypeLabel(areaType),
    areaType,
    paths,
    area: '0',
    areaAcres: '0',
    perimeter: '0',
    fillColor: getAreaTypeColor(areaType),
    strokeColor: '#000000',
  };
};

const parsePlacemark = (placemark, index) => {
  const fallbackName = `Imported Area ${index + 1}`;
  const name = firstChildText(placemark, 'name') || fallbackName;
  const imported = [];
  let skipped = 0;

  descendantsByLocalName(placemark, 'Polygon').forEach(polygon => {
    const outerBoundary = descendantsByLocalName(polygon, 'outerBoundaryIs')[0];
    const coordinateText = firstDescendantText(outerBoundary || polygon, 'coordinates');
    const paths = stripClosedRingDuplicate(parseCoordinateText(coordinateText));
    if (paths.length >= 3) imported.push(buildPolygonArea(name, paths));
    else skipped += 1;
  });

  descendantsByLocalName(placemark, 'LineString').forEach(line => {
    const paths = parseCoordinateText(firstDescendantText(line, 'coordinates'));
    if (paths.length >= 2) imported.push(buildPolylineArea(name, paths));
    else skipped += 1;
  });

  descendantsByLocalName(placemark, 'Point').forEach(point => {
    const paths = parseCoordinateText(firstDescendantText(point, 'coordinates')).slice(0, 1);
    if (paths.length === 1) imported.push(buildPointArea(name, paths));
    else skipped += 1;
  });

  return { imported, skipped };
};

export const parseKmlToAreaDrafts = (kmlText) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(kmlText, 'application/xml');
  const parseError = descendantsByLocalName(doc, 'parsererror')[0];
  if (parseError) {
    throw new Error('Could not parse KML file.');
  }

  const placemarks = descendantsByLocalName(doc, 'Placemark');
  const imported = [];
  let skipped = 0;

  placemarks.forEach((placemark, index) => {
    const result = parsePlacemark(placemark, index);
    imported.push(...result.imported);
    skipped += result.skipped;
  });

  return {
    areas: imported,
    skipped,
    placemarkCount: placemarks.length,
  };
};
